import type { Event, StackFrame } from "@sentry/nextjs";

// Sentry's Next.js SDK rewrites browser frames to app:///<path>, so code we serve shows up
// as app:///_next/static/chunks/... (bundles) or app:///scripts/necc.js (public scripts).
// Node frames are not rewritten this way, which is why the caller applies this to browser
// events only.
const FIRST_PARTY_FRAME_PATHS = ["/_next/", "/scripts/"];

const isFirstPartyFrame = (frame: StackFrame): boolean =>
    FIRST_PARTY_FRAME_PATHS.some((path) => (frame.filename || "").includes(path));

const stackFramesFor = (event: Event): StackFrame[] => {
    const frames: StackFrame[] = [];

    (event.exception?.values || []).forEach((value) => {
        frames.push(...(value.stacktrace?.frames || []));
    });

    return frames;
};

/**
 * Scripts injected into the page by something we don't ship — in-app webview bridges,
 * browser extensions, tag manager snippets — have no source URL of their own, so WebKit
 * attributes them to the document and the stack is a lone "global code" frame pointing at
 * the page itself. No frame belongs to a file we serve and there is nothing for us to fix,
 * so these are dropped instead of reported.
 *
 * Events without a stack (captured messages, network errors) are never treated as
 * third-party — only events we can positively attribute to someone else's code.
 */
export const isInjectedThirdPartyError = (event: Event): boolean => {
    const frames = stackFramesFor(event);

    return frames.length > 0 && !frames.some(isFirstPartyFrame);
};
